export default class AssetUploadService {
  constructor ($q, $rootScope, Upload, ApiService, FileMediaService) {
    'ngInject'

    this._$q = $q
    this._$rootScope = $rootScope
    this._Upload = Upload
    this._ApiService = ApiService
    this._FileMediaService = FileMediaService

    this._filenameRegex = /^(.+)_(l|p)\.(.+)$/i

    this._uploadQueue = []
    this._uploading = false
    this._pendingConflicts = []
  }

  _emitUploadStats () {
    // Calculate upload stats
    const stats = {
      filesRemaining: this._uploading ? 1 + this._uploadQueue.length : 0,
      bytesRemaining: 0
    }

    stats.bytesRemaining += this._uploading ? this._uploading.file.bytesRemaining : 0
    stats.bytesRemaining += this._uploadQueue.reduce((accumulator, obj) => accumulator + obj.file.bytesRemaining, 0)

    // And emit
    this._$rootScope.$emit('asset-upload-service:stats', stats)
  }

  _parseFilename (filename) {
    filename = filename.replace(/\s+/g, '_') // Replace all spaces with underscores
    const match = filename.match(this._filenameRegex)
    if (!match) {
      return this._$q.reject(new Error(`Could not parse filename: '${filename}'`))
    }

    const lowerCaseOrientation = match[2].toLowerCase()
    return {
      name: match[1],
      orientation: lowerCaseOrientation,
      fullFileName: `${match[1]}_${lowerCaseOrientation}.${match[3]}`
    }
  }

  _checkForExistingAsset (name, orientation) {
    // Find FileMedia with this name
    const allFileMedia = this._FileMediaService.getAll()
    const existingFileMedia = allFileMedia.find(fm => fm.name === name)

    if (existingFileMedia && Array.isArray(existingFileMedia.assets)) {
      const existingAsset = existingFileMedia.assets.find(
        asset => asset.orientation === orientation
      )

      if (existingAsset) {
        return {
          fileMediaId: existingFileMedia.id,
          fileMediaName: existingFileMedia.name,
          assetId: existingAsset.id,
          orientation: existingAsset.orientation
        }
      }
    }

    return null
  }

  _processUploadQueue () {
    // Get element to process (if any) and emit stats
    this._uploading = this._uploadQueue.shift()
    this._emitUploadStats()
    if (!this._uploading) {
      return
    }
    const { file, resolve, reject } = this._uploading

    this._$q.when()
      .then(() => {
        // Parse and validate file name
        return this._parseFilename(file.name)
      })
      .then(({ name, orientation, fullFileName }) => {
        // Create asset / request upload parameters
        return this._ApiService.call('asset/createAsset', {
          filename: fullFileName,
          name,
          orientation,
          bytes: file.size
        })
      }).then(uploadParameters => {
        // Just add file and mime type
        uploadParameters.file = file
        uploadParameters.fields['Content-Type'] = file.type && file.type !== '' ? file.type : 'application/octet-stream'

        // Start the upload
        return this._Upload.upload(uploadParameters)
          .progress(event => {
            // Emit stats
            file.bytesRemaining = event.total - event.loaded
            this._emitUploadStats()
          })
      })
      .then(() => {
        resolve()
      })
      .catch(err => {
        reject(err)
      })
      .finally(() => {
        this._processUploadQueue()
      })
  }

  uploadFile (file) {
    return this._$q.when()
      .then(() => {
        // Parse and validate file name
        return this._parseFilename(file.name)
      })
      .then(parsed => {
        // Check for existing asset with same name and orientation
        const existingAsset = this._checkForExistingAsset(parsed.name, parsed.orientation)

        if (existingAsset) {
          // Conflict detected - emit event for UI to handle
          return this._$q((resolve, reject) => {
            const conflict = {
              file,
              parsed,
              existingAsset,
              resolve,
              reject
            }

            this._pendingConflicts.push(conflict)
            this._$rootScope.$emit('asset-upload-service:conflict', conflict)
          })
        } else {
          // No conflict - proceed with upload
          return this._$q((resolve, reject) => {
            this._queueUpload(file, resolve, reject)
          })
        }
      })
  }

  _queueUpload (file, resolve, reject) {
    // Queue upload
    this._uploadQueue.push({
      file,
      resolve,
      reject
    })

    // Helper for stats
    file.bytesRemaining = file.size

    // Emit stats
    this._emitUploadStats()

    // Trigger processing of queue if not active
    if (!this._uploading) {
      this._processUploadQueue()
    }
  }

  resolveConflict (conflict, shouldReplace) {
    const index = this._pendingConflicts.indexOf(conflict)
    if (index > -1) {
      this._pendingConflicts.splice(index, 1)
    }

    if (shouldReplace) {
      // User confirmed - proceed with upload and replace
      this._queueUpload(conflict.file, conflict.resolve, conflict.reject)
    } else {
      // User cancelled
      conflict.reject(new Error('Upload cancelled by user'))
    }
  }
}
